import { Injectable } from '@angular/core'
import { Observable, concatMap, from, last, scan } from 'rxjs'
import { CatalogsService } from './catalogs.service'
import { uniqBy } from 'lodash'
import {
  NgModalServiceInterface,
  ModalResponse,
} from '../../models/modal.model'
import { NgNotificationServiceInterface } from '../../models/notification.model'
import {
  CatalogModalCode,
  CatalogNotificationCode,
  parseCatalogModal,
  parseCatalogNotification,
} from './libs/catalog-notification.lib'
import { CatalogSearchParams } from './models/catalog-search.model'
import { Catalog } from './models/catalog.entity'
import { Page } from '../../models/util.model'

@Injectable({
  providedIn: 'root',
})
export class CatalogsRepository {
  constructor(
    private catalogsService: CatalogsService,
    private ngModalService?: NgModalServiceInterface,
    private ngNotificationService?: NgNotificationServiceInterface,
  ) {}

  /**
   * Search catalogs and external data
   * @param searchParams - the search params
   * @param fields - the fields
   * @param listingData - the already loaded listing data
   * @returns the observable for search catalogs
   */
  searchCatalogs$(
    searchParams: CatalogSearchParams,
    returnAll?: boolean,
  ): Observable<Page<Catalog>> {
    const needsPagination =
      searchParams.limit !== undefined &&
      searchParams.limit > this.catalogsService.limit

    return needsPagination
      ? this._paginateCatalogs$(searchParams)
      : this._searchCatalogs$(searchParams, returnAll)
  }

  /**
   * Search catalogs with relative external data
   * @param searchParams - the search params
   * @param fields - the catalogs fields
   * @param listingData - the already loaded listing data
   * @returns the observable<Page<Catalog>> for search catalogs
   */
  private _searchCatalogs$(
    searchParams: CatalogSearchParams,
    returnAll?: boolean,
  ): Observable<Page<Catalog>> {
    return this.catalogsService.search$(searchParams, returnAll).pipe()
  }

  /**
   * Search more than 200 catalogs with relative external data
   * @param searchParams - the search params
   * @param fields - the catalogs fields
   * @param listingData - the already loaded listing data
   * @returns the observable<Page<Catalog>> for search catalogs
   */
  private _paginateCatalogs$(
    searchParams: CatalogSearchParams,
  ): Observable<Page<Catalog>> {
    const totalCount = searchParams.limit || this.catalogsService.limit
    const pagesCount = Math.ceil(totalCount / this.catalogsService.limit)

    return from(
      Array.from({ length: pagesCount }, (_, i) => ({
        ...searchParams,
        limit: this.catalogsService.limit,
        offset: i * this.catalogsService.limit,
      })),
    ).pipe(
      concatMap((pageParams) => this._searchCatalogs$(pageParams)),
      scan<Page<Catalog>, Page<Catalog>>(
        (acc, current) => ({
          totalCount: uniqBy(
            [...(acc.data || []), ...(current.data || [])],
            '_id',
          ).length,
          data: uniqBy([...(acc.data || []), ...(current.data || [])], '_id'),
        }),
        { totalCount: 0, data: [], extData: {} } as Page<Catalog>,
      ),
      last(),
    )
  }

  notify(options: { code?: CatalogNotificationCode; message?: string }): void {
    this.ngNotificationService?.show(
      options.message
        ? { title: 'Error', message: options.message, color: 'error' }
        : parseCatalogNotification(options.code),
    )
  }

  async alert(
    code: CatalogModalCode,
    catalog?: Catalog,
  ): Promise<ModalResponse> {
    const settings = parseCatalogModal(code, catalog)
    if (!this.ngModalService || !settings) {
      return 'closed'
    }
    return await this.ngModalService.showDialog(settings)
  }
}
